import type { CSSProperties } from 'react';

export type IconName =
  | 'search'
  | 'bell'
  | 'sun'
  | 'moon'
  | 'home'
  | 'wallet'
  | 'chart'
  | 'briefcase'
  | 'star'
  | 'news'
  | 'plus'
  | 'arrowUp'
  | 'arrowDown'
  | 'arrowRight'
  | 'cog'
  | 'deposit'
  | 'withdraw'
  | 'transfer'
  | 'sliders';

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  style?: CSSProperties;
}

export const Icon = ({ name, size = 16, stroke = 1.6, style }: IconProps) => {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: stroke,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style: { display: 'block', flexShrink: 0, ...style },
  };
  switch (name) {
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...props}>
          <path d="M6 8a6 6 0 1 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9z" />
          <path d="M10 21a2 2 0 0 0 4 0" />
        </svg>
      );
    case 'sun':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      );
    case 'moon':
      return (
        <svg {...props}>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      );
    case 'home':
      return (
        <svg {...props}>
          <path d="m3 11 9-8 9 8" />
          <path d="M5 10v10h14V10" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...props}>
          <path d="M3 7v12a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1H5a2 2 0 0 1 0-4h13" />
          <circle cx="17" cy="14" r="1.2" fill="currentColor" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...props}>
          <path d="M3 3v18h18" />
          <path d="m7 14 4-4 4 4 5-7" />
        </svg>
      );
    case 'briefcase':
      return (
        <svg {...props}>
          <rect x="3" y="7" width="18" height="13" rx="2" />
          <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
          <path d="M3 12h18" />
        </svg>
      );
    case 'star':
      return (
        <svg {...props}>
          <path d="m12 3 2.7 5.5 6 .9-4.4 4.3 1 6-5.3-2.8L6.7 19.7l1-6L3.3 9.4l6-.9z" />
        </svg>
      );
    case 'news':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="14" height="16" rx="2" />
          <path d="M17 8h3v10a2 2 0 0 1-2 2" />
          <path d="M7 8h6M7 12h6M7 16h4" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case 'arrowUp':
      return (
        <svg {...props}>
          <path d="m6 14 6-6 6 6" />
        </svg>
      );
    case 'arrowDown':
      return (
        <svg {...props}>
          <path d="m6 10 6 6 6-6" />
        </svg>
      );
    case 'arrowRight':
      return (
        <svg {...props}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    case 'cog':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
        </svg>
      );
    case 'deposit':
      return (
        <svg {...props}>
          <path d="M12 4v12" />
          <path d="m6 10 6 6 6-6" />
          <path d="M4 20h16" />
        </svg>
      );
    case 'withdraw':
      return (
        <svg {...props}>
          <path d="M12 20V8" />
          <path d="m6 14 6-6 6 6" />
          <path d="M4 4h16" />
        </svg>
      );
    case 'transfer':
      return (
        <svg {...props}>
          <path d="M7 4 3 8l4 4" />
          <path d="M3 8h13" />
          <path d="m17 20 4-4-4-4" />
          <path d="M21 16H8" />
        </svg>
      );
    case 'sliders':
      return (
        <svg {...props}>
          <path d="M4 6h11M4 12h7M4 18h13M19 4v4M14 10v4M20 16v4" />
        </svg>
      );
    default:
      return null;
  }
};
